import { OrderScenario } from 'components/order/model/Order';

export enum MessageItem {
    MESSAGE = 'MESSAGE'
}

export enum PresetAction {
    AWARDS = 'AWARDS',
    CHECKIN = 'CHECKIN',
    PREORDER = 'PREORDER',
    LOCATIONS = 'LOCATIONS',
    FAVOURITES = 'FAVOURITES',
    REFERRALS = 'REFERRALS',
    VOUCHER = 'VOUCHER',
    SECRET_DJ = 'SECRET_DJ',
    TABS = 'TABS',
    BARCODE = 'BARCODE'
}

export enum MessageAction {
    BROWSER = 'BROWSER',
    LOCATION = 'LOCATION',
    LOCATIONS = 'LOCATIONS',
    NATIVE = 'NATIVE',
    CUSTOM = 'CUSTOM',
    BARCODE = 'BARCODE',
    START_ORDER = 'START_ORDER',
    TABLE_ORDER = 'TABLE_ORDER',
    PAY_AT_TABLE = 'PAY_AT_TABLE',
    PREORDER = 'PREORDER',
    VOUCHER = 'VOUCHER',
    REFERRALS = 'REFERRALS',
    USER_DETAILS = 'USER_DETAILS',
    USER_PREFERENCES = 'USER_PREFERENCES',
    AWARDS = 'AWARDS',
    CHECKIN = 'CHECKIN',
    DINE_IN = 'DINE_IN',
    DELIVERY = 'DELIVERY',
    USER_ORDERS = 'USER_ORDERS',
    VIEW_SUBSCRIPTION_SCHEME = 'VIEW_SUBSCRIPTION_SCHEME'
}

export enum ContentModuleType {
    PRIMARY_ACTION = 'PRIMARY_ACTION',
    MARKETING = 'MARKETING',
    LOCATIONS = 'LOCATIONS',
    FAVOURITES = 'FAVOURITES',
    AWARDS = 'AWARDS',
    SECRET_DJ = 'SECRET_DJ',
    LOYALTY_BALANCE = 'LOYALTY_BALANCE'
}

interface IIndexableItem {
    index: number;
}

interface IMessageItem extends IFilterableItem, IIndexableItem {
    id?: string;
    type: MessageItem;
}

export interface IPresetMessageItem extends IMessageItem {
    action: PresetAction;
    name: string;
    properties: {
        imageUrl: string;
        // overlay text properties
        title?: string;
        subtitle?: string;
        subtitleTwo?: string;
        invertTextColour?: boolean;
        showTextBackgroundBar?: boolean;
    };
}

interface ICustomMessageItem extends IMessageItem {
    action: MessageAction.CUSTOM;
    name: string;
    properties: {
        url: string;
        // no overlay text properties - all content comes from URL
    };
}

export interface IBrowserMessageItem extends IMessageItem {
    action: MessageAction.BROWSER;
    name: string;
    properties: {
        url: string;
        imageUrl: string;
        requiresLocationSelection?: boolean;
        // overlay text properties
        title?: string;
        subtitle?: string;
        subtitleTwo?: string;
        invertTextColour?: boolean;
        showTextBackgroundBar?: boolean;
    };
}

export interface ILocationMessageItem extends IMessageItem {
    action: MessageAction.LOCATION;
    name: string;
    properties: {
        locationId: string;
        imageUrl: string;
        // overlay text properties
        title?: string;
        subtitle?: string;
        subtitleTwo?: string;
        invertTextColour?: boolean;
        showTextBackgroundBar?: boolean;
    };
}

export interface ILocationsListMessageItem extends IMessageItem {
    action: MessageAction.LOCATIONS;
    name: string;
    properties: {
        imageUrl: string;
        // overlay text properties
        title?: string;
        subtitle?: string;
        subtitleTwo?: string;
        invertTextColour?: boolean;
        showTextBackgroundBar?: boolean;
    };
}

export interface INativeMessageItem extends IMessageItem {
    action: MessageAction.NATIVE;
    name: string;
    properties: {
        imageUrl: string;
        // overlay text properties
        title?: string;
        subtitle?: string;
        subtitleTwo?: string;
        invertTextColour?: boolean;
        showTextBackgroundBar?: boolean;
        content: {
            imageUrl: string;
            pageTitle: string;
            title: string;
            subtitle: string;
            body: string;
            buttonTitle: string;
            buttonUrl: string;
        };
    };
}

export enum EStartOrderDestination {
    MENU = 'MENU',
    PRODUCT = 'PRODUCT',
    CATEGORY = 'CATEGORY'
}

export interface IStartOrderMessageItem extends IMessageItem {
    action: MessageAction.START_ORDER;
    name: string;
    properties: {
        imageUrl: string;
        selectableScenarios: OrderScenario[];
        focus: {
            type: EStartOrderDestination;
            properties?: {
                categoryId: string;
                productId: string;
            };
        };
        selectableLocations?: {
            tags?: string[];
        };
    };
}

export interface IViewSubscriptionSchemeMessageItem extends IMessageItem {
    action: MessageAction.VIEW_SUBSCRIPTION_SCHEME;
    name: string;
    properties: {
        imageUrl: string;
        title?: string;
        subtitle?: string;
        subtitleTwo?: string;
        invertTextColour?: boolean;
        showTextBackgroundBar?: boolean;
        subscriptionSchemeId: string;
    };
}

export interface IDefaultMessageItem extends IMessageItem {
    action: MessageAction.BARCODE;
    name: string;
    properties: {
        locationId: string;
        imageUrl: string;
        title?: string;
        subtitle?: string;
        subtitleTwo?: string;
        invertTextColour?: boolean;
        showTextBackgroundBar?: boolean;
    };
}

export interface IItemContext {
    visibleFrom?: string;
    visibleTo?: string;
    locationIds?: string[];
    segmentIds?: string[];
    authenticated?: boolean;
    unauthenticated?: boolean;
    excludingSegmentIds?: string[];
    schedule?: IModuleSchedule[];
}

interface IFilterableItem {
    context: IItemContext;
}

export enum Height {
    SINGLE = 'SINGLE',
    DOUBLE = 'DOUBLE'
}

export enum EItemShape {
    RECTANGLE = 'RECTANGLE',
    SQUARE = 'SQUARE'
}

export enum SecondaryAction {
    BARCODE = 'BARCODE',
    LOCATIONS = 'LOCATIONS',
    FAVOURITES = 'FAVOURITES',
    SUBSCRIPTIONS = 'SUBSCRIPTIONS'
}

export interface IDisplay {
    height?: Height | EHeightOption;
    itemShape?: EItemShape;
    itemsOnScreen?: number;
}

export enum EHeightOption {
    SINGLE_HEIGHT_RECTANGLE = 'SINGLE_HEIGHT_RECTANGLE',
    DOUBLE_HEIGHT_RECTANGLE = 'DOUBLE_HEIGHT_RECTANGLE',
    SMALL_SQUARE = 'SMALL_SQUARE',
    LARGE_SQUARE = 'LARGE_SQUARE'
}

export interface ISecondaryAction {
    title: string;
    action: SecondaryAction;
}

export type PrimaryActionType =
    | 'AWARDS'
    | 'CHECKIN'
    | 'PREORDER'
    | 'LOCATIONS'
    | 'FAVOURITES'
    | 'START_ORDER'
    | 'TABLE_ORDER'
    | 'PAY_AT_TABLE'
    | 'VOUCHER'
    | 'REFERRALS'
    | 'BARCODE'
    | 'USER_DETAILS'
    | 'USER_PREFERENCES'
    | 'TABS'
    | 'BOOK_TABLE'
    | 'BOOK_ROOM'
    | 'BROWSER'
    | 'DINE_IN'
    | 'DELIVERY'
    | 'USER_ORDERS';

export interface PrimaryActionItem {
    id?: string;
    title: string;
    type: PrimaryActionType;
    name: string;
    context?: IItemContext;
    properties?: {
        useCheckinLocation?: boolean;
        url?: string;
    };
    index: number;
}
interface IFilterableItemsModule {
    items: IFilterableItem[];
}

interface IModuleWithSchedule {
    context?: Pick<IItemContext, 'visibleTo' | 'visibleFrom' | 'schedule'>;
}

export interface IModuleSchedule {
    // number of seconds from midnight
    timeTo: number;
    // number of seconds from midnight
    timeFrom: number;
    daysOfWeek?: number;
}

interface IBaseModule {
    id: string;
    type: ContentModuleType;
    index: number;
    title?: string;
    secondaryAction?: ISecondaryAction;
    display?: IDisplay;
}

export type MarketingMessageItem =
    | IPresetMessageItem
    | IBrowserMessageItem
    | ILocationMessageItem
    | ILocationsListMessageItem
    | INativeMessageItem
    | ICustomMessageItem
    | IStartOrderMessageItem
    | IViewSubscriptionSchemeMessageItem
    | IDefaultMessageItem;
interface IFilterableItemsModule {
    items: IFilterableItem[];
}
export interface IPrimaryAction extends IBaseModule {
    type: ContentModuleType.PRIMARY_ACTION;
    items: PrimaryActionItem[];
    context?: Pick<IItemContext, 'visibleTo' | 'visibleFrom' | 'schedule'>;
}

export interface IAwardsModule extends IBaseModule, IModuleWithSchedule {
    type: ContentModuleType.AWARDS;
    properties?: {
        includePerkIds?: string[];
        excludePerkIds?: string[];
    };
}

export interface ILocationsModule extends IBaseModule, IModuleWithSchedule {
    type: ContentModuleType.LOCATIONS;
    properties: {
        defaultRegion: {
            latitude: number;
            longitude: number;
            width: number;
        };
    };
}

export interface IFavouritesModule extends IBaseModule, IModuleWithSchedule {
    type: ContentModuleType.FAVOURITES;
}

export interface IMarketingModule extends IBaseModule, IFilterableItemsModule {
    type: ContentModuleType.MARKETING;
    items: MarketingMessageItem[];
    context?: IItemContext;
}

export interface ISecretDjModule extends IBaseModule, IModuleWithSchedule {
    type: ContentModuleType.SECRET_DJ;
}

export interface ILoyaltyBalanceModule extends IBaseModule, IModuleWithSchedule {
    type: ContentModuleType.LOYALTY_BALANCE;
    properties: {
        visualisation: {
            textColour: string;
            backgroundOverlayColour?: string;
            subtitleText?: string;
            textAlignment: 'LEFT' | 'CENTER' | 'RIGHT';
            subtitleTwoText?: string;
            verticalAlignment: 'TOP' | 'MIDDLE' | 'BOTTOM';
            backgroundImageUrl?: string;
            terms?: string;
            description?: string;
        };
    };
}

export type IMarketingAction =
    | PresetAction
    | MessageAction.BROWSER
    | MessageAction.LOCATION
    | MessageAction.LOCATIONS
    | MessageAction.NATIVE
    | MessageAction.CUSTOM
    | MessageAction.START_ORDER
    | MessageAction.VIEW_SUBSCRIPTION_SCHEME
    | MessageAction.BARCODE;

export type ContentModule =
    | IMarketingModule
    | IFavouritesModule
    | ILocationsModule
    | IAwardsModule
    | IPrimaryAction
    | ISecretDjModule
    | ILoyaltyBalanceModule;

function isBrowserMessage(message: any): message is IBrowserMessageItem {
    return !!message && message.action === MessageAction.BROWSER;
}
function isLocationMessage(message: any): message is ILocationMessageItem {
    return !!message && message.action === MessageAction.LOCATION;
}
function isLocationsListMessage(message: any): message is ILocationsListMessageItem {
    return !!message && message.action === MessageAction.LOCATIONS;
}
function isNativeMessage(message: any): message is INativeMessageItem {
    return !!message && message.action === MessageAction.NATIVE;
}
function isTableOrderMessage(message: any): message is INativeMessageItem {
    return !!message && message.action === MessageAction.TABLE_ORDER;
}
function isCustomMessage(message: any): message is ICustomMessageItem {
    return !!message && message.action === MessageAction.CUSTOM;
}
function isPresetMessage(message: any): message is IPresetMessageItem {
    return !!message && Object.values(PresetAction).includes(message.action);
}
function isStartOrderMessage(message: any): message is IStartOrderMessageItem {
    return !!message && message.action === MessageAction.START_ORDER;
}
function isViewSubscriptionSchemeMessage(message: any): message is IViewSubscriptionSchemeMessageItem {
    return !!message && message.action === MessageAction.VIEW_SUBSCRIPTION_SCHEME;
}
const defaultMessages = [
    MessageAction.PREORDER,
    MessageAction.PAY_AT_TABLE,
    MessageAction.VOUCHER,
    MessageAction.REFERRALS,
    MessageAction.USER_DETAILS,
    MessageAction.CHECKIN,
    MessageAction.USER_PREFERENCES,
    MessageAction.BARCODE,
    MessageAction.AWARDS,
    MessageAction.DINE_IN,
    MessageAction.DELIVERY,
    MessageAction.USER_ORDERS
];

function isDefaultMarketingMessage(message: any): message is IDefaultMessageItem {
    return !!message && defaultMessages.includes(message.action);
}

export function isLocationsModule(module: any): module is ILocationsModule {
    const defaultRegionProperties: string[] = ['latitude', 'longitude', 'width'];
    return (
        !!module &&
        !!module.properties &&
        !!module.properties.defaultRegion &&
        defaultRegionProperties.reduce<boolean>((i, j) => i && j in module.properties.defaultRegion, true)
    );
}

export function isLoyaltyBalanceModule(module: any): module is ILoyaltyBalanceModule {
    return (
        !!module && module.type === ContentModuleType.LOYALTY_BALANCE && !!module.properties?.visualisation
    );
}

export function isMarketingModule(module: ContentModule): module is IMarketingModule {
    return module.type === ContentModuleType.MARKETING;
}

export function displayToMarketingHeight(display?: IDisplay) {
    if (display?.itemShape) {
        if (display.itemShape === EItemShape.RECTANGLE) {
            return display.height === Height.DOUBLE
                ? EHeightOption.DOUBLE_HEIGHT_RECTANGLE
                : EHeightOption.SINGLE_HEIGHT_RECTANGLE;
        }
        return display.itemsOnScreen === 3 ? EHeightOption.SMALL_SQUARE : EHeightOption.LARGE_SQUARE;
    }

    return display ? display.height : EHeightOption.SINGLE_HEIGHT_RECTANGLE;
}

export const marketingHeightToDisplay = (display?: IDisplay) => {
    switch (display?.height) {
        case EHeightOption.SINGLE_HEIGHT_RECTANGLE: {
            return {
                itemShape: EItemShape.RECTANGLE,
                height: Height.SINGLE
            };
        }
        case EHeightOption.DOUBLE_HEIGHT_RECTANGLE: {
            return {
                itemShape: EItemShape.RECTANGLE,
                height: Height.DOUBLE
            };
        }
        case EHeightOption.SMALL_SQUARE: {
            return {
                itemShape: EItemShape.SQUARE,
                itemsOnScreen: 3
            };
        }
        case EHeightOption.LARGE_SQUARE: {
            return {
                itemShape: EItemShape.SQUARE,
                itemsOnScreen: 2
            };
        }
        default: {
            return { height: display?.height };
        }
    }
};

const checkDisplayEquality = (shape: EHeightOption, display?: IDisplay) => {
    const convertedDisplayHeight = displayToMarketingHeight(display);

    return convertedDisplayHeight === shape || display?.height === shape;
};

export const isSquaredDisplay = (display?: IDisplay) => {
    const convertedDisplayHeight = displayToMarketingHeight(display);
    const squaredHeightValues: (EHeightOption | Height)[] = [
        EHeightOption.SMALL_SQUARE,
        EHeightOption.LARGE_SQUARE
    ];
    if (display?.height && squaredHeightValues.includes(display?.height)) {
        return true;
    }
    return squaredHeightValues.includes(convertedDisplayHeight);
};

export const isSmallSquareDisplay = (display?: IDisplay) =>
    checkDisplayEquality(EHeightOption.SMALL_SQUARE, display);

export const isLargeSquareDisplay = (display?: IDisplay) =>
    checkDisplayEquality(EHeightOption.LARGE_SQUARE, display);

export const isSingleHeightDisplay = (display?: IDisplay) =>
    checkDisplayEquality(EHeightOption.SINGLE_HEIGHT_RECTANGLE, display);

export const isDoubleHeightDisplay = (display?: IDisplay) =>
    checkDisplayEquality(EHeightOption.DOUBLE_HEIGHT_RECTANGLE, display);

export const marketingTypeguards = {
    isBrowserMessage,
    isLocationMessage,
    isLocationsListMessage,
    isNativeMessage,
    isCustomMessage,
    isPresetMessage,
    isStartOrderMessage,
    isTableOrderMessage,
    isViewSubscriptionSchemeMessage,
    isDefaultMarketingMessage
};
